import { Button as MuiButton, styled, CSSObject, alpha } from "@mui/material";
import { brandColors } from "../../../../stories/helpers/ColorTokenHelper";
import { ColorTokensEnum } from "../../../../enums/tokens/colorTokensEnum";
import { ShapeTokensEnum } from "../../../../enums/tokens/shapeTokensEnum";
import SelectButtonProps from "@/app/interfaces/props/SelectButtonProps";
import { StateTokensEnum } from "../../../../enums/tokens/stateTokensEnum";
import typographyTokens from "../../../../stories/tokens/typography-tokens.json";
import { TypographyTokensEnum } from "../../../../enums/tokens/typographyTokensEnum";
import { radii } from "../../../../stories/helpers/RadiusTokenHelper";
import { RadiusTokensEnum } from "../../../../enums/tokens/radiusTokensEnum";

const DEFAULT_ICON_SIZE = 20;

const StyledSelectButton = styled(MuiButton, {
  shouldForwardProp: (prop) => !["ownerState"].includes(prop as string),
})<{ ownerState: SelectButtonProps }>(({ ownerState }): CSSObject => {
  const body1Typography = (typographyTokens[TypographyTokensEnum.BODY_1] ||
    {}) as CSSObject;

  const resolvedIconSize =
    typeof ownerState.iconSize === "number"
      ? `${ownerState.iconSize}px`
      : ownerState.iconSize;

  return {
    ...body1Typography,
    textTransform: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",

    ...(resolvedIconSize && {
      "& .MuiButton-startIcon, & .MuiButton-endIcon": {
        "& > *:first-of-type": { fontSize: resolvedIconSize },
        "& svg, & img": {
          width: resolvedIconSize,
          height: resolvedIconSize,
          fontSize: resolvedIconSize,
        },
      },
    }),
    borderRadius:
      ownerState.shape === ShapeTokensEnum.PILL
        ? radii[RadiusTokensEnum.PILL]
        : radii[RadiusTokensEnum.SM],
    padding: "4px 16px",
    height: "42px",
    minWidth: "76px",

    // --- DEFAULT STATE ---
    ...(!ownerState.selected && {
      backgroundColor: brandColors[ColorTokensEnum.WHITE],
      color: brandColors[ColorTokensEnum.DARKEST_GREY],
      border: `1px solid ${brandColors[ColorTokensEnum.MED_GREY]}`,

      "&:hover": {
        backgroundColor: brandColors[ColorTokensEnum.WHITE],
        color: brandColors[ColorTokensEnum.HOVER],
        border: `2px solid ${brandColors[ColorTokensEnum.HOVER]}`,
      },

      "&:focus-visible": {
        backgroundColor: brandColors[ColorTokensEnum.WHITE],
        color: brandColors[ColorTokensEnum.DARKEST_GREY],
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
        boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
        outline: "none",
      },
    }),

    // --- SELECTED STATE ---
    ...(ownerState.selected && {
      backgroundColor: brandColors[ColorTokensEnum.INTERACTIVE],
      color: brandColors[ColorTokensEnum.WHITE],
      border: `2px solid ${brandColors[ColorTokensEnum.INTERACTIVE]}`,

      "&:hover": {
        backgroundColor: brandColors[ColorTokensEnum.HOVER],
        border: `2px solid ${brandColors[ColorTokensEnum.HOVER]}`,
      },

      "&:focus-visible": {
        backgroundColor: brandColors[ColorTokensEnum.INTERACTIVE],
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
        boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
      },
    }),

    // --- FOCUS STATE OVERRIDE ---
    ...(ownerState.state === StateTokensEnum.FOCUS && {
      ...(ownerState.selected
        ? {
            backgroundColor: brandColors[ColorTokensEnum.INTERACTIVE],
            border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
            boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,

            "&:hover": {
              backgroundColor: brandColors[ColorTokensEnum.INTERACTIVE],
              border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
              boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
            },
          }
        : {
            backgroundColor: brandColors[ColorTokensEnum.WHITE],
            color: brandColors[ColorTokensEnum.DARKEST_GREY],
            border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
            boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,

            "&:hover": {
              backgroundColor: brandColors[ColorTokensEnum.WHITE],
              color: brandColors[ColorTokensEnum.DARKEST_GREY],
              border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
              boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
            },
          }),
    }),

    // --- HOVER STATE OVERRIDE ----
    ...(ownerState.state === StateTokensEnum.HOVER && {
      ...(ownerState.selected
        ? {
            backgroundColor: brandColors[ColorTokensEnum.HOVER],
            border: `2px solid ${brandColors[ColorTokensEnum.HOVER]}`,

            "&:focus": {
              backgroundColor: brandColors[ColorTokensEnum.HOVER],
              border: `2px solid ${brandColors[ColorTokensEnum.HOVER]}`,
            },
          }
        : {
            backgroundColor: brandColors[ColorTokensEnum.WHITE],
            color: brandColors[ColorTokensEnum.HOVER],
            border: `2px solid ${brandColors[ColorTokensEnum.HOVER]}`,

            "&:focus": {
              backgroundColor: brandColors[ColorTokensEnum.WHITE],
              color: brandColors[ColorTokensEnum.HOVER],
              border: `2px solid ${brandColors[ColorTokensEnum.HOVER]}`,
            },
          }),
    }),
  };
});

export const SelectButton = ({
  label,
  state = StateTokensEnum.DEFAULT,
  selected = false,
  iconSize = DEFAULT_ICON_SIZE,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  shape = ShapeTokensEnum.RECTANGLE,
  ...props
}: SelectButtonProps) => {
  return (
    <StyledSelectButton
      ownerState={{ label, state, selected, shape, iconSize }}
      variant="contained"
      disableElevation
      startIcon={LeftIcon ? <LeftIcon /> : undefined}
      endIcon={RightIcon ? <RightIcon /> : undefined}
      {...props}
    >
      {label}
    </StyledSelectButton>
  );
};
